/**
 * Client for the Weekly Release Train document API (/api/train).
 *
 * Reads are open; the save (PUT) goes through the same Go-Live editor session,
 * so it reuses the Go-Live bearer token (localStorage) — which is what makes
 * editing work inside the ADO iframe, where third-party cookies are blocked.
 */
import type { TrainConfig } from "@/lib/trainGen"

export interface TrainDoc {
  html: string
  config: TrainConfig | null
  /** Shared per-week task completion: { "<weekMonday ISO>": ["<taskKey>", …] }. */
  completions?: Record<string, string[]>
  updated_at: string | null
  updated_by: string | null
}

const BASE = "/api/train"
// Same key the Go-Live client uses — one editor session across both tabs.
const TOKEN_KEY = "golive-token"

const getToken = () => {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const token = getToken()
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string>) }
  if (init?.body) headers["Content-Type"] = "application/json"
  if (token) headers["Authorization"] = `Bearer ${token}`
  const res = await fetch(`${BASE}${path}`, { credentials: "include", ...init, headers })
  if (!res.ok) {
    let detail = res.statusText
    try {
      detail = (await res.json()).detail ?? detail
    } catch {
      /* ignore */
    }
    const err = new Error(detail) as Error & { status: number }
    err.status = res.status
    throw err
  }
  return (await res.json()) as T
}

export const train = {
  get: () => req<TrainDoc>(""),
  save: (html: string, config?: TrainConfig | null) =>
    req<TrainDoc>("", { method: "PUT", body: JSON.stringify({ html, config }) }),
  /** Toggle one task's completion for one week — merged server-side so concurrent
   *  editors don't overwrite each other (unlike the whole-doc save). */
  setCompletion: (week: string, task: string, done: boolean) =>
    req<TrainDoc>("/completion", { method: "POST", body: JSON.stringify({ week, task, done }) }),
  reset: () => req<TrainDoc>("/reset", { method: "POST" }),
}
